import type { AxiosRequestConfig } from "axios";

// Paged list responses seen recently, so a grid's export can ask the same endpoint for every page.
// Pages hand the response rows to the grid as they are, so a grid row is the very object recorded here.

interface RecordedList {
  config: AxiosRequestConfig;
  // Where the paging lives: a POST body ({ page, limit, filters }) or GET query params.
  in: "data" | "params";
  rows: object[];
  total: number;
}

const MAX_RECORDED = 30;
const recent: RecordedList[] = [];

const parseBody = (data: unknown): Record<string, unknown> | null => {
  if (typeof data === "string") {
    try {
      return JSON.parse(data) as Record<string, unknown>;
    } catch {
      return null;
    }
  }

  return data && typeof data === "object" ? (data as Record<string, unknown>) : null;
};

const isPaged = (value: Record<string, unknown> | null) =>
  !!value && value.page !== undefined && value.limit !== undefined;

export const recordListResponse = (config: AxiosRequestConfig, payload: unknown): void => {
  const body = parseBody(config.data);
  const where = isPaged(body) ? "data" : isPaged(config.params ?? null) ? "params" : null;
  const response = payload as { data?: unknown; total?: unknown } | null;
  if (!where || !response || !Array.isArray(response.data)) return;
  recent.unshift({
    config: { ...config, data: body ?? undefined },
    in: where,
    rows: response.data as object[],
    total: Number(response.total) || response.data.length,
  });
  recent.length = Math.min(recent.length, MAX_RECORDED);
};

const idOf = (row: object) => {
  const record = row as { _id?: unknown; id?: unknown };

  return record._id ?? record.id;
};

/** The recorded list the grid is showing: same row objects first, then the same ids. */
export const findRecordedList = (gridRows: object[]): RecordedList | null => {
  if (!gridRows.length) return null;
  const byReference = recent.find((list) => gridRows.some((row) => list.rows.includes(row)));
  if (byReference) return byReference;
  const ids = new Set(
    gridRows
      .map(idOf)
      .filter((id) => id !== undefined)
      .map(String),
  );
  if (!ids.size) return null;

  return recent.find((list) => list.rows.some((row) => ids.has(String(idOf(row))))) ?? null;
};

export type RawRequest = (config: AxiosRequestConfig) => Promise<unknown>;

/** Every page of a recorded list (up to `maxRows`), using the endpoint's own filters. */
export const fetchAllPages = async (
  list: RecordedList,
  request: RawRequest,
  maxRows: number,
  onProgress?: (loaded: number, total: number) => void,
): Promise<{ rows: object[]; total: number }> => {
  // 100 is the smallest cap any admin list endpoint puts on `limit`.
  const limit = 100;
  const rows: object[] = [];
  let total = list.total;
  for (let page = 1; rows.length < Math.min(total, maxRows); page++) {
    const paging = { page, limit };
    const config: AxiosRequestConfig =
      list.in === "data"
        ? { ...list.config, data: { ...(list.config.data as object), ...paging } }
        : { ...list.config, params: { ...list.config.params, ...paging } };
    const response = (await request(config)) as { data?: object[]; total?: number };
    const pageRows = Array.isArray(response?.data) ? response.data : [];
    if (!pageRows.length) break;
    rows.push(...pageRows);
    total = Number(response.total) || total;
    onProgress?.(rows.length, total);
  }

  return { rows: rows.slice(0, maxRows), total };
};
